/**
 * Police-Ebene Risiko & Deckung — the SINGLE SOURCE OF TRUTH for the branchenspezifischen
 * Ist-Attribute (per IGB2B code) and the Soll-Deckung & Gap regelwerk on the Police level
 * (P2 Placement Slice 2; Detailkonzept Ausschreibung V3.0 Ch 5 / 6.2 / 6.5 / 7.2 / 9).
 *
 * Mirrors the Slice-1 catalog-as-code idiom (kunde-risikoprofil.ts): adding a branch field or a
 * Soll rule needs no migration. Reuses the Slice-1 field machinery — `RisikoprofilFeldBasis`,
 * `validateRisikoprofilAttributes`, `countFilledFelder`, `RisikoprofilFeldInput` (web) — against
 * the per-branch catalog here.
 *
 * MODELING (slice-2 design D2/D3):
 *   - Per-branch Ist catalog as code, keyed by IGB2B branchencode: BRANCHEN_IST_KATALOG. A policy's
 *     Ist-Attribute live in `policy_risk_attributes.attributes_json`, validated against the branch
 *     catalog of the policy's `igb2b_code`. Family heads (10000, 20000, …) carry an EMPTY catalog
 *     and trigger a "Sparte präzisieren" reclassification; 60000 is NOT a pure head (it has a Ch-5
 *     catalog). The one sensitive Ch-5 attribute (91000 Gesundheitspruefung-Flag) is NOT stored on
 *     the policy — the tab mirrors customer_risk_profile.gesundheitspruefung_status read-only.
 *   - Soll-Deckung as code: SOLL_DECKUNG_KATALOG (the default rule per branch, like Slice 1's
 *     BASISSPARTEN_CHECKLISTE). Stored Soll/Gap lives in `policy_target_coverage` keyed by a natural
 *     `rule_key`; Slice 3 turns this code catalog into a `target_coverage_catalog` table.
 *
 * [Source: docs/product/p2-slices/slice-2-police-ebene-design.md §3]
 * [Source: docs/product/2026-09-24_Brokly_Detailkonzept_Ausschreibung_V3.0.txt Ch 5]
 * [Source: docs/product/2026-09-24_IGB2B_InsuranceType.csv (branchencode vocabulary)]
 * [Source: packages/shared/src/kunde-risikoprofil.ts (catalog-as-code precedent + reused seams)]
 */

import type { SubjectKind } from './policy';
import {
  BASISSPARTEN_AMPEL,
  KANTON_CODES,
  RISIKOPROFIL_FELD_TYP,
  RISIKOPROFIL_OPTIONEN_LABELS,
  igb2bFamilyOf,
} from './kunde-risikoprofil';
import type {
  BasisspartenAmpel,
  GesundheitspruefungStatus,
  RisikoprofilFeldBasis,
} from './kunde-risikoprofil';

// ── Branch field definition (the per-IGB2B-code Ist catalog entry) ──────────────

/**
 * One atomic branch Ist-field. Extends the Slice-1 field base with the owning `branchencode`,
 * so the exact same input + validator + counters work against this catalog.
 */
export interface BranchenFeldDefinition extends RisikoprofilFeldBasis {
  branchencode: string;
}

// ── Vocabularies (Ch 5 / 6.2 / 7.2) ─────────────────────────────────────────────

/** Where an Ist-attribute set came from (policy_risk_attributes.source CHECK). */
export const POLICE_RISIKO_SOURCE = {
  MANUELL: 'manuell',
  IDP: 'idp',
  ZEFIX: 'zefix',
  FRAGEBOGEN: 'fragebogen',
  POLICE: 'police',
  PROMOTION: 'promotion',
} as const;
export type PoliceRisikoSource = (typeof POLICE_RISIKO_SOURCE)[keyof typeof POLICE_RISIKO_SOURCE];
export const POLICE_RISIKO_SOURCE_VALUES: readonly PoliceRisikoSource[] = [
  POLICE_RISIKO_SOURCE.MANUELL,
  POLICE_RISIKO_SOURCE.IDP,
  POLICE_RISIKO_SOURCE.ZEFIX,
  POLICE_RISIKO_SOURCE.FRAGEBOGEN,
  POLICE_RISIKO_SOURCE.POLICE,
  POLICE_RISIKO_SOURCE.PROMOTION,
];

/** The Soll-Deckung status of one rule for a policy (policy_target_coverage.status CHECK). */
export const DECKUNG_STATUS = {
  COVERED: 'covered',
  GAP: 'gap',
  OVERINSURED: 'overinsured',
  NOT_APPLICABLE: 'not_applicable',
  UNKNOWN: 'unknown',
} as const;
export type DeckungStatus = (typeof DECKUNG_STATUS)[keyof typeof DECKUNG_STATUS];
export const DECKUNG_STATUS_VALUES: readonly DeckungStatus[] = [
  DECKUNG_STATUS.COVERED,
  DECKUNG_STATUS.GAP,
  DECKUNG_STATUS.OVERINSURED,
  DECKUNG_STATUS.NOT_APPLICABLE,
  DECKUNG_STATUS.UNKNOWN,
];
export const DECKUNG_STATUS_LABELS: Record<DeckungStatus, string> = {
  [DECKUNG_STATUS.COVERED]: 'Gedeckt',
  [DECKUNG_STATUS.GAP]: 'Luecke',
  [DECKUNG_STATUS.OVERINSURED]: 'Ueberversichert',
  [DECKUNG_STATUS.NOT_APPLICABLE]: 'Nicht anwendbar',
  [DECKUNG_STATUS.UNKNOWN]: 'Unbekannt',
};

/** How a Soll-Deckung row was evaluated (policy_target_coverage.evaluation_source CHECK). */
export const DECKUNG_EVALUATION_SOURCE = {
  MANUELL: 'manuell',
  REGELWERK: 'regelwerk',
  PROMOTION: 'promotion',
} as const;
export type DeckungEvaluationSource =
  (typeof DECKUNG_EVALUATION_SOURCE)[keyof typeof DECKUNG_EVALUATION_SOURCE];
export const DECKUNG_EVALUATION_SOURCE_VALUES: readonly DeckungEvaluationSource[] = [
  DECKUNG_EVALUATION_SOURCE.MANUELL,
  DECKUNG_EVALUATION_SOURCE.REGELWERK,
  DECKUNG_EVALUATION_SOURCE.PROMOTION,
];

/** The catalog Pflicht-status of a Soll-Deckung rule (Ch 5). */
export const SOLL_PFLICHT_STATUS = {
  PFLICHT: 'pflicht',
  BEDINGT_PFLICHT: 'bedingt_pflicht',
  EMPFEHLUNG: 'empfehlung',
  OPTIONAL: 'optional',
} as const;
export type SollPflichtStatus = (typeof SOLL_PFLICHT_STATUS)[keyof typeof SOLL_PFLICHT_STATUS];
export const SOLL_PFLICHT_STATUS_VALUES: readonly SollPflichtStatus[] = [
  SOLL_PFLICHT_STATUS.PFLICHT,
  SOLL_PFLICHT_STATUS.BEDINGT_PFLICHT,
  SOLL_PFLICHT_STATUS.EMPFEHLUNG,
  SOLL_PFLICHT_STATUS.OPTIONAL,
];
export const SOLL_PFLICHT_STATUS_LABELS: Record<SollPflichtStatus, string> = {
  [SOLL_PFLICHT_STATUS.PFLICHT]: 'Pflicht',
  [SOLL_PFLICHT_STATUS.BEDINGT_PFLICHT]: 'Bedingt Pflicht',
  [SOLL_PFLICHT_STATUS.EMPFEHLUNG]: 'Empfehlung',
  [SOLL_PFLICHT_STATUS.OPTIONAL]: 'Optional',
};

/** The Ampel colour shown per Deckungs-row (derived). Re-export of the Slice-1 Ampel vocab. */
export const DECKUNG_AMPEL = BASISSPARTEN_AMPEL;
export type DeckungAmpel = BasisspartenAmpel;

// ── Thresholds + events + gated mirror (design §3) ──────────────────────────────

/** Gap in % below which a gap is "zu pruefen" (gelb); at/above it is a rote Luecke. */
export const DECKUNG_GAP_GELB_SCHWELLE_PROZENT = 20;
/** Overinsurance in % above the Soll beyond which a cover is flagged overinsured (Q20). */
export const DECKUNG_UEBERVERSICHERT_SCHWELLE_PROZENT = 20;

// ── Size / count caps for Police-Risiko data (PR #43 review #13) ────────────────
/** Max insured objects (non-NULL insurance_object_ref) per policy AND per branch snapshot. The policy-level NULL-ref set does not count. */
export const POLICE_RISIKO_OBJEKTE_MAX = 50;
/** Max |target_value| / |actual_value| / overrides — mirrors numeric(14,2). */
export const DECKUNG_WERT_MAX = 999999999999.99;
/** gap_percent is numeric(7,2): clamp to this before writing. */
export const DECKUNG_GAP_PERCENT_MAX = 99999.99;

/** Domain events (emitted through the activity/audit + pg-boss mechanism). */
export const POLICE_RISIKO_EVENTS = {
  RISK_ATTRIBUTES_UPDATED: 'policy.risk_attributes_updated',
  TARGET_COVERAGE_UPDATED: 'policy.target_coverage_updated',
} as const;
export type PoliceRisikoEvent =
  (typeof POLICE_RISIKO_EVENTS)[keyof typeof POLICE_RISIKO_EVENTS];

/**
 * IGB2B codes whose Ist tab mirrors the Kunde gesundheitspruefung_status read-only (D2): the
 * flag is NOT stored on the policy. Today only Einzelleben (91000).
 */
export const POLICE_RISIKO_GESUNDHEIT_MIRROR_CODES = ['91000'] as const;

// ── Option labels (Ch 5 select/multiselect values → German labels) ──────────────

/**
 * Flat option-value → German label map for the branch catalog. Spreads the Slice-1 map first
 * (so shared values like reise_* / wert_* / natur_* keep one label), then the Police-only values.
 * Kanton codes self-label (handled in policeRisikoOptionLabel).
 */
export const POLICE_RISIKO_OPTIONEN_LABELS: Record<string, string> = {
  ...RISIKOPROFIL_OPTIONEN_LABELS,
  // 11000 Motorfahrzeug
  mfz_privat: 'Privat',
  mfz_geschaeftlich: 'Geschaeftlich',
  mfz_gewerblich_transport: 'Gewerblicher Transport',
  mfz_taxi_mietwagen: 'Taxi / Mietwagen',
  // 12000 Luftfahrt
  luft_privat: 'Privat',
  luft_gewerblich: 'Gewerblich',
  luft_schulung: 'Schulung',
  luft_arbeitsflug: 'Arbeitsflug',
  lizenz_lapl: 'LAPL',
  lizenz_ppl: 'PPL',
  lizenz_cpl: 'CPL',
  lizenz_atpl: 'ATPL',
  // 13000 Wasserfahrzeug
  boot_motorboot: 'Motorboot',
  boot_segelboot: 'Segelboot',
  boot_yacht: 'Yacht',
  boot_sonstiges: 'Sonstiges',
  fahrgebiet_binnen: 'Binnengewaesser',
  fahrgebiet_kuesten: 'Kuestengewaesser',
  fahrgebiet_hochsee: 'Hochsee',
  // 21000 Betriebshaftpflicht
  ausland_keine: 'Keine Auslandstaetigkeit',
  ausland_europa: 'Europa',
  ausland_weltweit_ohne_usa_kanada: 'Weltweit ohne USA/Kanada',
  ausland_inkl_usa_kanada: 'Weltweit inkl. USA/Kanada',
  // 22000 Gebaeudehaftpflicht
  geb_wohn: 'Wohngebaeude',
  geb_gewerbe: 'Gewerbegebaeude',
  geb_gemischt: 'Gemischte Nutzung',
  geb_industrie: 'Industriegebaeude',
  zustand_gut: 'Gut',
  zustand_mittel: 'Mittel',
  zustand_sanierungsbedarf: 'Sanierungsbedarf',
  // 24000 Organhaftpflicht
  rechtsform_ag: 'AG',
  rechtsform_gmbh: 'GmbH',
  rechtsform_genossenschaft: 'Genossenschaft',
  rechtsform_verein_stiftung: 'Verein / Stiftung',
  rechtsform_andere: 'Andere',
  // 25000 Bauherrenhaftpflicht
  bau_neubau: 'Neubau',
  bau_umbau_sanierung: 'Umbau / Sanierung',
  bau_abbruch: 'Abbruch',
  bau_tiefbau: 'Tiefbau',
  // 35000 Schuelerunfall
  schule_kindergarten: 'Kindergarten',
  schule_primar: 'Primarstufe',
  schule_sek1: 'Sekundarstufe I',
  schule_sek2: 'Sekundarstufe II',
  schule_tertiaer: 'Tertiaerstufe',
  // 36000 UVGZ
  uvgz_basis: 'Basis',
  uvgz_erweitert: 'Erweitert',
  uvgz_komfort: 'Komfort',
  // 41000 KVG
  franchise_300: 'CHF 300',
  franchise_500: 'CHF 500',
  franchise_1000: 'CHF 1’000',
  franchise_1500: 'CHF 1’500',
  franchise_2000: 'CHF 2’000',
  franchise_2500: 'CHF 2’500',
  // 42000 KTG
  wartefrist_14: '14 Tage',
  wartefrist_30: '30 Tage',
  wartefrist_60: '60 Tage',
  wartefrist_90: '90 Tage',
  wartefrist_180: '180 Tage',
  // 51000 Warenversicherung
  transport_strasse: 'Strasse',
  transport_schiene: 'Schiene',
  transport_luft: 'Luft',
  transport_see: 'See',
  transport_binnenschiff: 'Binnenschiff',
  reichweite_schweiz: 'Schweiz',
  reichweite_europa: 'Europa',
  reichweite_weltweit: 'Weltweit',
  // 58000 Kunstgegenstaende
  kunst_gemaelde: 'Gemaelde',
  kunst_skulptur: 'Skulptur',
  kunst_antiquitaeten: 'Antiquitaeten',
  kunst_sammlung: 'Sammlung',
  kunst_andere: 'Andere',
  // 60000 Sachversicherung
  geb_efh: 'Einfamilienhaus',
  geb_mfh: 'Mehrfamilienhaus',
  bauweise_massiv: 'Massivbauweise',
  bauweise_holz: 'Holzbauweise',
  bauweise_gemischt: 'Gemischte Bauweise',
  nutzung_wohnen: 'Wohnen',
  nutzung_gewerbe: 'Gewerbe',
  nutzung_gemischt: 'Gemischt',
  nutzung_leerstand: 'Leerstand',
  sicher_brandmelde: 'Brandmeldeanlage',
  sicher_sprinkler: 'Sprinkleranlage',
  sicher_alarm: 'Alarmanlage',
  sicher_video: 'Videoueberwachung',
  sicher_keine: 'Keine',
  natur_sturm_hagel: 'Sturm / Hagel',
  // 65000 Wertsachen
  aufbewahrung_tresor: 'Tresor',
  aufbewahrung_bankschliessfach: 'Bankschliessfach',
  aufbewahrung_offen: 'Offene Aufbewahrung',
  // 67000 Betriebsunterbrechung
  haftungszeit_6: '6 Monate',
  haftungszeit_12: '12 Monate',
  haftungszeit_18: '18 Monate',
  haftungszeit_24: '24 Monate',
  abhaengigkeit_gering: 'Gering',
  abhaengigkeit_mittel: 'Mittel',
  abhaengigkeit_hoch: 'Hoch',
  // 71000 Bauwesen
  bauart_hochbau: 'Hochbau',
  bauart_tiefbau: 'Tiefbau',
  bauart_ingenieurbau: 'Ingenieurbau',
  // 74000 EDV
  bu_it_gering: 'Gering',
  bu_it_mittel: 'Mittel',
  bu_it_hoch: 'Hoch',
  // 82000 Rechtsschutz
  rs_privat: 'Privat',
  rs_verkehr: 'Verkehr',
  rs_arbeit: 'Arbeit',
  rs_miete_immobilien: 'Miete / Immobilien',
  rs_vertrag: 'Vertrag',
  rs_betrieb: 'Betrieb',
  rs_internet: 'Internet',
  // 83500 Reiseversicherung
  reisedauer_bis_2w: 'bis 2 Wochen',
  reisedauer_2w_8w: '2 bis 8 Wochen',
  reisedauer_ueber_8w: 'ueber 8 Wochen',
  // 85000 Epidemie
  epi_event: 'Events',
  epi_gastro: 'Gastronomie',
  epi_kultur: 'Kultur',
  epi_tourismus: 'Tourismus',
  epi_andere: 'Andere',
  ausfall_gering: 'Gering',
  ausfall_mittel: 'Mittel',
  ausfall_hoch: 'Hoch',
  // 87000 Cyber
  cyber_firewall: 'Firewall',
  cyber_mfa: 'Multi-Faktor-Authentisierung',
  cyber_backup: 'Backup-Konzept',
  cyber_isms: 'ISMS-Zertifizierung',
  cyber_awareness_training: 'Awareness-Training',
  cyber_edr: 'Endpoint Detection & Response',
  daten_gering: 'Gering',
  daten_mittel: 'Mittel',
  daten_hoch_besondere: 'Hoch (besondere Personendaten)',
  cloud_keine: 'Keine Cloud-Nutzung',
  cloud_teilweise: 'Teilweise',
  cloud_ueberwiegend: 'Ueberwiegend',
  // 88000 Kaution
  kaution_werk: 'Werkgarantie',
  kaution_liefer: 'Liefergarantie',
  kaution_miet: 'Mietkaution',
  kaution_zoll: 'Zollkaution',
  kaution_andere: 'Andere',
  // 92000 Freizuegigkeit
  fz_bis_50k: 'bis CHF 50k',
  fz_50k_100k: 'CHF 50k bis 100k',
  fz_100k_250k: 'CHF 100k bis 250k',
  fz_250k_500k: 'CHF 250k bis 500k',
  fz_ueber_500k: 'ueber CHF 500k',
  // 93000 Kollektivleben
  plan_bvg_minimum: 'BVG-Minimum',
  plan_umhuellend: 'Umhuellend',
  plan_kader_1e: 'Kaderplan 1e',
  // 94000 Hypotheken
  hypo_bis_500k: 'bis CHF 500k',
  hypo_500k_1m: 'CHF 500k bis 1 Mio.',
  hypo_1m_2m: 'CHF 1 bis 2 Mio.',
  hypo_ueber_2m: 'ueber CHF 2 Mio.',
  zins_variabel: 'Variabel',
  zins_saron: 'SARON',
  zins_fest_bis_5j: 'Festhypothek bis 5 Jahre',
  zins_fest_ueber_5j: 'Festhypothek ueber 5 Jahre',
};

/** German label for a branch option value (Kanton codes label as themselves; unknown = value). */
export function policeRisikoOptionLabel(value: string): string {
  if ((KANTON_CODES as readonly string[]).includes(value)) return value;
  return POLICE_RISIKO_OPTIONEN_LABELS[value] ?? value;
}

// ── IGB2B code → German label (built from the IGB2B InsuranceType CSV) ───────────

/**
 * The 59 IGB2B branchencodes → their Deutsche Bezeichnung (the CSV's column 3). Used by the web
 * Sparte select (PolicyEditDialog / reclassification) and as the authority the branch-catalog
 * test checks its codes against. Family heads included.
 */
export const IGB2B_CODE_LABELS: Record<string, string> = {
  '10000': 'Fahrzeugversicherung',
  '11000': 'Motorfahrzeugversicherung',
  '12000': 'Luftfahrtzeugversicherung',
  '13000': 'Wasserfahrzeugversicherung',
  '14000': 'Motorradversicherung',
  '20000': 'Haftpflichtversicherungen',
  '21000': 'Betriebs- und Berufshaftpflicht Versicherung',
  '22000': 'Gebaeudehaftpflicht Versicherung',
  '23000': 'Vermoegensschadenhaftpflicht Versicherung',
  '24000': 'Organhaftpflicht Versicherung',
  '25000': 'Bauherrenhaftpflicht Versicherung',
  '26000': 'Privathaftpflicht Versicherung',
  '29900': 'Uebrige Haftpflichtversicherungen',
  '30000': 'Unfallversicherungen',
  '31000': 'Einzelunfall Versicherung',
  '33000': 'Kollektivunfall Versicherung',
  '34000': 'Landwirtschaftliche Unfallversicherung',
  '35000': 'Schuelerunfallversicherung',
  '36000': 'Unfallzusatzversicherung (UVGZ)',
  '38000': 'Obligatorische Unfallversicherung (UVG/OUFL)',
  '39900': 'Uebrige Unfallversicherungen',
  '40000': 'Krankenversicherung',
  '41000': 'Krankenversicherung Einzel',
  '42000': 'Kollektiv Krankentaggeldversicherung',
  '43000': 'Uebrige Krankenversicherung',
  '50000': 'Transportversicherungen',
  '51000': 'Warenversicherung',
  '52000': 'Valorenversicherung',
  '53000': 'Reisegepaeckversicherung',
  '54000': 'Frachtfuehrer Versicherung',
  '55000': 'Spediteurhaftpflicht Versicherung',
  '56000': 'Transport Kasko Versicherung',
  '58000': 'Kunstgegenstaende Versicherung',
  '59900': 'Uebrige Transportversicherungen',
  '60000': 'Sachversicherungen (Kombinationen)',
  '65000': 'Wertsachenversicherung',
  '67000': 'Betriebsunterbrechungsversicherung',
  '69900': 'Uebrige Sachversicherungen',
  '70000': 'Technische Versicherungen',
  '71000': 'Bauwesen Versicherung',
  '72000': 'Montageversicherung',
  '73000': 'Maschinenversicherung',
  '74000': 'EDV-Anlagen',
  '75000': 'Allg. technische Anlagen-Versicherung (ATA)',
  '79900': 'Uebrige technische Versicherungen',
  '80000': 'Versicherungen uebrige Branchen',
  '81000': 'Kreditversicherungen',
  '82000': 'Rechtschutzversicherung',
  '83500': 'Assistance und Reiseversicherung',
  '85000': 'Epidemie-, Hygiene- und Pandemieversicherung',
  '87000': 'Cyberversicherung',
  '88000': 'Garantie- und Kautionsversicherung',
  '89900': 'Uebrigen Versicherungen',
  '90000': 'Lebensversicherungen (Kombinationen)',
  '91000': 'Einzellebensversicherung (Kombinationen)',
  '92000': 'Freizuegigkeitspolice',
  '93000': 'Kollektivlebensversicherung (Kombinationen)',
  '94000': 'Hypotheken',
  '99900': 'Uebrige Lebensversicherungen und Finanzprodukte',
};

/** German label for an IGB2B branchencode (unknown = the raw code). */
export function igb2bCodeLabel(code: string): string {
  return IGB2B_CODE_LABELS[code] ?? code;
}

// ── BRANCHEN_IST_KATALOG (per-branch Ist fields, Ch 5) ──────────────────────────

const F = RISIKOPROFIL_FELD_TYP;
const KANTON = KANTON_CODES;

/** Build the BranchenFeldDefinition list for one code, stamping the branchencode on each field. */
function felder(
  branchencode: string,
  defs: readonly Omit<BranchenFeldDefinition, 'branchencode'>[],
): readonly BranchenFeldDefinition[] {
  return defs.map((d) => ({ ...d, branchencode }));
}

/**
 * The per-IGB2B-code Ist-Attribute catalog (Ch 5). 51 codes / 162 fields (the leaf codes that
 * carry Ch-5 attributes + the eight "Übrige" catch-all codes with one Freitext field each). The
 * eight PURE family heads (10000/20000/30000/40000/50000/70000/80000/90000) are NOT keys here —
 * `branchenFelderFuer` returns [] for them. 60000 is NOT a pure head (it has a Ch-5 catalog).
 *
 * NOTE (deviation, flagged to the reviewer): the design header says "163 fields"; the literal
 * field list below counts 162 (the design's own text, transcribed verbatim — the 91000
 * gesundheitspruefung_status mirror is read-only and NOT a stored catalog field, per D2). The
 * catalog is the authority; the count spec asserts the shipped 162.
 */
export const BRANCHEN_IST_KATALOG: Record<string, readonly BranchenFeldDefinition[]> = {
  // ═══ Fahrzeugversicherungen ════════════════════════════════════════════════════
  '11000': felder('11000', [
    { key: 'marke', label: 'Marke', typ: F.TEXT },
    { key: 'modell', label: 'Modell', typ: F.TEXT },
    { key: 'fahrgestellnummer', label: 'Fahrgestellnummer', typ: F.TEXT },
    { key: 'erstzulassungsjahr', label: 'Erstzulassungsjahr', typ: F.NUMBER },
    { key: 'fahrzeugwert', label: 'Fahrzeugwert', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'verwendungszweck', label: 'Verwendungszweck', typ: F.SELECT, options: ['mfz_privat', 'mfz_geschaeftlich', 'mfz_gewerblich_transport', 'mfz_taxi_mietwagen'] },
    { key: 'jahreskilometer', label: 'Jahreskilometer', typ: F.NUMBER },
    { key: 'anzahl_lenker', label: 'Anzahl Lenker', typ: F.NUMBER },
    { key: 'alter_juengster_lenker', label: 'Alter juengster Lenker', typ: F.NUMBER },
    { key: 'garagierungsort_kanton', label: 'Garagierungsort (Kanton)', typ: F.SELECT, options: KANTON },
    { key: 'bonus_malus_stufe', label: 'Bonus-Malus-Stufe', typ: F.TEXT },
    { key: 'leasing_finanzierung', label: 'Leasing / Finanzierung', typ: F.BOOLEAN },
  ]),
  '12000': felder('12000', [
    { key: 'fluggeraetetyp', label: 'Fluggeraetetyp', typ: F.TEXT },
    { key: 'baujahr', label: 'Baujahr', typ: F.NUMBER },
    { key: 'einsatzzweck', label: 'Einsatzzweck', typ: F.SELECT, options: ['luft_privat', 'luft_gewerblich', 'luft_schulung', 'luft_arbeitsflug'] },
    { key: 'jahresflugstunden', label: 'Jahresflugstunden', typ: F.NUMBER },
    { key: 'pilotenlizenz_stufe', label: 'Pilotenlizenz-Stufe', typ: F.SELECT, options: ['lizenz_lapl', 'lizenz_ppl', 'lizenz_cpl', 'lizenz_atpl'] },
    { key: 'stationierungsflugplatz', label: 'Stationierungsflugplatz', typ: F.TEXT },
  ]),
  '13000': felder('13000', [
    { key: 'bootstyp', label: 'Bootstyp', typ: F.SELECT, options: ['boot_motorboot', 'boot_segelboot', 'boot_yacht', 'boot_sonstiges'] },
    { key: 'laenge_m', label: 'Laenge', typ: F.NUMBER, einheit: 'm' },
    { key: 'motorisierung_ps', label: 'Motorisierung', typ: F.NUMBER, einheit: 'PS' },
    { key: 'baujahr', label: 'Baujahr', typ: F.NUMBER },
    { key: 'fahrgebiet', label: 'Fahrgebiet', typ: F.SELECT, options: ['fahrgebiet_binnen', 'fahrgebiet_kuesten', 'fahrgebiet_hochsee'] },
    { key: 'liegeplatz_winterlager', label: 'Liegeplatz / Winterlager', typ: F.TEXT },
  ]),
  '14000': felder('14000', [
    { key: 'hubraum_ccm', label: 'Hubraum', typ: F.NUMBER, einheit: 'ccm' },
    { key: 'baujahr', label: 'Baujahr', typ: F.NUMBER },
    { key: 'fahrerfahrung_jahre_kat_a', label: 'Fahrerfahrung Kat. A (Jahre)', typ: F.NUMBER },
    { key: 'saisonkennzeichen', label: 'Saisonkennzeichen', typ: F.BOOLEAN },
  ]),
  // ═══ Haftpflichtversicherungen ═══════════════════════════════════════════════════
  '21000': felder('21000', [
    { key: 'taetigkeitsfeld_berufsgruppe', label: 'Taetigkeitsfeld / Berufsgruppe', typ: F.TEXT },
    { key: 'umsatz_praemienbasis', label: 'Umsatz (Praemienbasis)', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'auslandstaetigkeit', label: 'Auslandstaetigkeit', typ: F.SELECT, options: ['ausland_keine', 'ausland_europa', 'ausland_weltweit_ohne_usa_kanada', 'ausland_inkl_usa_kanada'] },
    { key: 'subunternehmer_personalverleih', label: 'Subunternehmer / Personalverleih', typ: F.BOOLEAN },
    { key: 'produktehaftung_exposure', label: 'Produktehaftung-Exposure', typ: F.TEXTAREA },
  ]),
  '22000': felder('22000', [
    { key: 'gebaeudetyp', label: 'Gebaeudetyp', typ: F.SELECT, options: ['geb_wohn', 'geb_gewerbe', 'geb_gemischt', 'geb_industrie'] },
    { key: 'anzahl_einheiten', label: 'Anzahl Einheiten', typ: F.NUMBER },
    { key: 'baujahr', label: 'Baujahr', typ: F.NUMBER },
    { key: 'baulicher_zustand', label: 'Baulicher Zustand', typ: F.SELECT, options: ['zustand_gut', 'zustand_mittel', 'zustand_sanierungsbedarf'] },
  ]),
  '23000': felder('23000', [
    { key: 'taetigkeitsart', label: 'Taetigkeitsart', typ: F.TEXT },
    { key: 'gewuenschte_deckungssumme', label: 'Gewuenschte Deckungssumme', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'retroaktivdatum', label: 'Retroaktivdatum', typ: F.DATE },
  ]),
  '24000': felder('24000', [
    { key: 'bilanzsumme', label: 'Bilanzsumme', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'rechtsform', label: 'Rechtsform', typ: F.SELECT, options: ['rechtsform_ag', 'rechtsform_gmbh', 'rechtsform_genossenschaft', 'rechtsform_verein_stiftung', 'rechtsform_andere'] },
    { key: 'boersenkotierung', label: 'Boersenkotierung', typ: F.BOOLEAN },
    { key: 'anzahl_vr_gl_mitglieder', label: 'Anzahl VR-/GL-Mitglieder', typ: F.NUMBER },
  ]),
  '25000': felder('25000', [
    { key: 'art_bauvorhaben', label: 'Art des Bauvorhabens', typ: F.SELECT, options: ['bau_neubau', 'bau_umbau_sanierung', 'bau_abbruch', 'bau_tiefbau'] },
    { key: 'bausumme', label: 'Bausumme', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'geplante_bauzeit_monate', label: 'Geplante Bauzeit (Monate)', typ: F.NUMBER },
  ]),
  '26000': felder('26000', [
    { key: 'haushaltsgroesse', label: 'Haushaltsgroesse', typ: F.NUMBER },
    { key: 'haustierhaltung', label: 'Haustierhaltung', typ: F.BOOLEAN },
    { key: 'hunderasse', label: 'Hunderasse', typ: F.TEXT },
    { key: 'angestellte_privathaushalt', label: 'Angestellte im Privathaushalt', typ: F.BOOLEAN },
  ]),
  // ═══ Unfallversicherungen ════════════════════════════════════════════════════════
  '31000': felder('31000', [
    { key: 'berufsgruppe_gefahrenklasse', label: 'Berufsgruppe / Gefahrenklasse', typ: F.TEXT },
    { key: 'risikosportarten', label: 'Risikosportarten', typ: F.TEXTAREA },
    { key: 'gewuenschte_versicherungssumme', label: 'Gewuenschte Versicherungssumme', typ: F.NUMBER, einheit: 'CHF' },
  ]),
  '33000': felder('33000', [
    { key: 'lohnsumme', label: 'Lohnsumme', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'anzahl_versicherte_personen', label: 'Anzahl versicherte Personen', typ: F.NUMBER },
    { key: 'branchen_gefahrenklasse', label: 'Branchen-Gefahrenklasse', typ: F.TEXT },
  ]),
  '34000': felder('34000', [
    { key: 'betriebsart', label: 'Betriebsart', typ: F.TEXT },
    { key: 'anzahl_mitarbeitende_familienangehoerige', label: 'Anzahl Mitarbeitende / Familienangehoerige', typ: F.NUMBER },
  ]),
  '35000': felder('35000', [
    { key: 'schulstufe', label: 'Schulstufe', typ: F.SELECT, options: ['schule_kindergarten', 'schule_primar', 'schule_sek1', 'schule_sek2', 'schule_tertiaer'] },
    { key: 'anzahl_versicherte_schueler', label: 'Anzahl versicherte Schueler', typ: F.NUMBER },
  ]),
  '36000': felder('36000', [
    { key: 'lohnsumme_ueber_uvg_hoechstbetrag', label: 'Lohnsumme ueber UVG-Hoechstbetrag', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'gewuenschtes_zusatzleistungsniveau', label: 'Gewuenschtes Zusatzleistungsniveau', typ: F.SELECT, options: ['uvgz_basis', 'uvgz_erweitert', 'uvgz_komfort'] },
  ]),
  '38000': felder('38000', [
    { key: 'lohnsumme', label: 'Lohnsumme', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'anzahl_mitarbeitende_vollzeit', label: 'Anzahl Mitarbeitende Vollzeit', typ: F.NUMBER },
    { key: 'anzahl_mitarbeitende_teilzeit', label: 'Anzahl Mitarbeitende Teilzeit', typ: F.NUMBER },
    { key: 'suva_tarifgruppe', label: 'SUVA-Tarifgruppe', typ: F.TEXT },
  ]),
  // ═══ Krankenversicherungen ═══════════════════════════════════════════════════════
  '41000': felder('41000', [
    { key: 'alter', label: 'Alter', typ: F.NUMBER },
    { key: 'wohnkanton_praemienregion', label: 'Wohnkanton / Praemienregion', typ: F.SELECT, options: KANTON },
    { key: 'gewuenschte_franchise', label: 'Gewuenschte Franchise', typ: F.SELECT, options: ['franchise_300', 'franchise_500', 'franchise_1000', 'franchise_1500', 'franchise_2000', 'franchise_2500'] },
    { key: 'zusatzversicherungsbedarf', label: 'Zusatzversicherungsbedarf', typ: F.TEXTAREA },
  ]),
  '42000': felder('42000', [
    { key: 'lohnsumme', label: 'Lohnsumme', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'anzahl_mitarbeitende', label: 'Anzahl Mitarbeitende', typ: F.NUMBER },
    { key: 'gewuenschte_wartefrist_tage', label: 'Gewuenschte Wartefrist', typ: F.SELECT, options: ['wartefrist_14', 'wartefrist_30', 'wartefrist_60', 'wartefrist_90', 'wartefrist_180'] },
    { key: 'bisherige_absenzenquote_prozent', label: 'Bisherige Absenzenquote', typ: F.NUMBER, einheit: '%' },
  ]),
  // ═══ Transportversicherungen ═══════════════════════════════════════════════════════
  '51000': felder('51000', [
    { key: 'warenart_gefahrgutklasse', label: 'Warenart / Gefahrgutklasse', typ: F.TEXT },
    { key: 'transportmittel', label: 'Transportmittel', typ: F.MULTISELECT, options: ['transport_strasse', 'transport_schiene', 'transport_luft', 'transport_see', 'transport_binnenschiff'] },
    { key: 'transportvolumen_pa', label: 'Transportvolumen p.a.', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'reichweite', label: 'Reichweite', typ: F.SELECT, options: ['reichweite_schweiz', 'reichweite_europa', 'reichweite_weltweit'] },
  ]),
  '52000': felder('52000', [
    { key: 'werttransportart', label: 'Werttransportart', typ: F.TEXT },
    { key: 'hoechstwert_pro_transport', label: 'Hoechstwert pro Transport', typ: F.NUMBER, einheit: 'CHF' },
  ]),
  '53000': felder('53000', [
    { key: 'reisehaeufigkeit', label: 'Reisehaeufigkeit', typ: F.SELECT, options: ['reise_selten', 'reise_gelegentlich', 'reise_haeufig'] },
    { key: 'typische_reiseziele', label: 'Typische Reiseziele', typ: F.TEXTAREA },
  ]),
  '54000': felder('54000', [
    { key: 'fuhrparkgroesse', label: 'Fuhrparkgroesse', typ: F.NUMBER },
    { key: 'jaehrliches_frachtvolumen', label: 'Jaehrliches Frachtvolumen', typ: F.NUMBER, einheit: 'CHF' },
  ]),
  '55000': felder('55000', [
    { key: 'umsatz', label: 'Umsatz', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'taetigkeitsgebiet', label: 'Taetigkeitsgebiet', typ: F.SELECT, options: ['reichweite_schweiz', 'reichweite_europa', 'reichweite_weltweit'] },
  ]),
  '56000': felder('56000', [
    { key: 'fahrzeug_warenwert_transport', label: 'Fahrzeug-/Warenwert im Transport', typ: F.NUMBER, einheit: 'CHF' },
  ]),
  '58000': felder('58000', [
    { key: 'objektwert', label: 'Objektwert', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'objektkategorie', label: 'Objektkategorie', typ: F.SELECT, options: ['kunst_gemaelde', 'kunst_skulptur', 'kunst_antiquitaeten', 'kunst_sammlung', 'kunst_andere'] },
    { key: 'lagerort', label: 'Lagerort', typ: F.TEXT },
    { key: 'sicherheitsvorkehrungen', label: 'Sicherheitsvorkehrungen', typ: F.TEXTAREA },
  ]),
  // ═══ Sachversicherungen ════════════════════════════════════════════════════════════
  '60000': felder('60000', [
    { key: 'gebaeudeart', label: 'Gebaeudeart', typ: F.SELECT, options: ['geb_efh', 'geb_mfh', 'geb_gewerbe', 'geb_gemischt', 'geb_industrie'] },
    { key: 'baujahr', label: 'Baujahr', typ: F.NUMBER },
    { key: 'bauweise', label: 'Bauweise', typ: F.SELECT, options: ['bauweise_massiv', 'bauweise_holz', 'bauweise_gemischt'] },
    { key: 'versicherungswert_neuwert', label: 'Versicherungswert (Neuwert)', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'nutzung', label: 'Nutzung', typ: F.SELECT, options: ['nutzung_wohnen', 'nutzung_gewerbe', 'nutzung_gemischt', 'nutzung_leerstand'] },
    { key: 'anzahl_einheiten', label: 'Anzahl Einheiten', typ: F.NUMBER },
    { key: 'sicherheitseinrichtungen', label: 'Sicherheitseinrichtungen', typ: F.MULTISELECT, options: ['sicher_brandmelde', 'sicher_sprinkler', 'sicher_alarm', 'sicher_video', 'sicher_keine'] },
    { key: 'naturgefahren_exposition', label: 'Naturgefahren-Exposition', typ: F.MULTISELECT, options: ['natur_hochwasser', 'natur_erdbeben', 'natur_lawinen', 'natur_sturm_hagel', 'natur_keine'] },
    { key: 'vorschaeden', label: 'Vorschaeden', typ: F.TEXTAREA },
  ]),
  '65000': felder('65000', [
    { key: 'objektart', label: 'Objektart', typ: F.SELECT, options: ['wert_schmuck', 'wert_kunst', 'wert_sammlungen', 'wert_andere'] },
    { key: 'gesamtwert', label: 'Gesamtwert', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'aufbewahrungsort', label: 'Aufbewahrungsort', typ: F.SELECT, options: ['aufbewahrung_tresor', 'aufbewahrung_bankschliessfach', 'aufbewahrung_offen'] },
  ]),
  '67000': felder('67000', [
    { key: 'gewuenschte_haftungszeit_monate', label: 'Gewuenschte Haftungszeit (Monate)', typ: F.SELECT, options: ['haftungszeit_6', 'haftungszeit_12', 'haftungszeit_18', 'haftungszeit_24'] },
    { key: 'versicherte_rohertragssumme', label: 'Versicherte Rohertragssumme', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'abhaengigkeit_schluessellieferanten_kunden', label: 'Abhaengigkeit Schluessellieferanten / -kunden', typ: F.SELECT, options: ['abhaengigkeit_gering', 'abhaengigkeit_mittel', 'abhaengigkeit_hoch'] },
  ]),
  // ═══ Technische Versicherungen ═══════════════════════════════════════════════════════
  '71000': felder('71000', [
    { key: 'bausumme', label: 'Bausumme', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'bauart', label: 'Bauart', typ: F.SELECT, options: ['bauart_hochbau', 'bauart_tiefbau', 'bauart_ingenieurbau'] },
    { key: 'geplante_bauzeit_monate', label: 'Geplante Bauzeit (Monate)', typ: F.NUMBER },
    { key: 'anzahl_baubeteiligte', label: 'Anzahl Baubeteiligte', typ: F.NUMBER },
  ]),
  '72000': felder('72000', [
    { key: 'wert_montageobjekt', label: 'Wert Montageobjekt', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'montagedauer_monate', label: 'Montagedauer (Monate)', typ: F.NUMBER },
  ]),
  '73000': felder('73000', [
    { key: 'maschinenpark_wert', label: 'Maschinenpark-Wert', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'durchschnittsalter_jahre', label: 'Durchschnittsalter (Jahre)', typ: F.NUMBER },
    { key: 'wartungsvertrag_vorhanden', label: 'Wartungsvertrag vorhanden', typ: F.BOOLEAN },
  ]),
  '74000': felder('74000', [
    { key: 'hardware_anlagenwert', label: 'Hardware-/Anlagenwert', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'bu_risiko_it_ausfall', label: 'BU-Risiko IT-Ausfall', typ: F.SELECT, options: ['bu_it_gering', 'bu_it_mittel', 'bu_it_hoch'] },
  ]),
  '75000': felder('75000', [
    { key: 'anlagentyp', label: 'Anlagentyp', typ: F.TEXT },
    { key: 'wert', label: 'Wert', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'baujahr', label: 'Baujahr', typ: F.NUMBER },
  ]),
  // ═══ Versicherungen uebriger Branchen ════════════════════════════════════════════════
  '81000': felder('81000', [
    { key: 'forderungsvolumen', label: 'Forderungsvolumen', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'debitorenstruktur', label: 'Debitorenstruktur', typ: F.TEXTAREA },
    { key: 'laender_exposure_debitoren', label: 'Laender-Exposure Debitoren', typ: F.TEXTAREA },
  ]),
  '82000': felder('82000', [
    { key: 'benoetigte_rechtsgebiete', label: 'Benoetigte Rechtsgebiete', typ: F.MULTISELECT, options: ['rs_privat', 'rs_verkehr', 'rs_arbeit', 'rs_miete_immobilien', 'rs_vertrag', 'rs_betrieb', 'rs_internet'] },
    { key: 'anzahl_versicherte_personen', label: 'Anzahl versicherte Personen', typ: F.NUMBER },
  ]),
  '83500': felder('83500', [
    { key: 'reisehaeufigkeit', label: 'Reisehaeufigkeit', typ: F.SELECT, options: ['reise_selten', 'reise_gelegentlich', 'reise_haeufig'] },
    { key: 'zieldestinationen', label: 'Zieldestinationen', typ: F.TEXTAREA },
    { key: 'reisedauer', label: 'Reisedauer', typ: F.SELECT, options: ['reisedauer_bis_2w', 'reisedauer_2w_8w', 'reisedauer_ueber_8w'] },
    { key: 'anzahl_versicherte_personen', label: 'Anzahl versicherte Personen', typ: F.NUMBER },
  ]),
  '85000': felder('85000', [
    { key: 'branche_exponiert', label: 'Exponierte Branche', typ: F.SELECT, options: ['epi_event', 'epi_gastro', 'epi_kultur', 'epi_tourismus', 'epi_andere'] },
    { key: 'umsatzausfallrisiko', label: 'Umsatzausfallrisiko', typ: F.SELECT, options: ['ausfall_gering', 'ausfall_mittel', 'ausfall_hoch'] },
  ]),
  '87000': felder('87000', [
    { key: 'anzahl_endpoints', label: 'Anzahl Endpoints', typ: F.NUMBER },
    { key: 'anzahl_server', label: 'Anzahl Server', typ: F.NUMBER },
    { key: 'sicherheitsmassnahmen', label: 'Sicherheitsmassnahmen', typ: F.MULTISELECT, options: ['cyber_firewall', 'cyber_mfa', 'cyber_backup', 'cyber_isms', 'cyber_awareness_training', 'cyber_edr'] },
    { key: 'datenumfang_sensitivitaet', label: 'Datenumfang / Sensitivitaet', typ: F.SELECT, options: ['daten_gering', 'daten_mittel', 'daten_hoch_besondere'] },
    { key: 'bisherige_cyber_vorfaelle', label: 'Bisherige Cyber-Vorfaelle', typ: F.NUMBER },
    { key: 'cloud_nutzung', label: 'Cloud-Nutzung', typ: F.SELECT, options: ['cloud_keine', 'cloud_teilweise', 'cloud_ueberwiegend'] },
  ]),
  '88000': felder('88000', [
    { key: 'kautionsvolumen', label: 'Kautionsvolumen', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'vertragsart', label: 'Vertragsart', typ: F.SELECT, options: ['kaution_werk', 'kaution_liefer', 'kaution_miet', 'kaution_zoll', 'kaution_andere'] },
  ]),
  // ═══ Lebensversicherungen ═══════════════════════════════════════════════════════════
  '91000': felder('91000', [
    { key: 'alter', label: 'Alter', typ: F.NUMBER },
    { key: 'gewuenschte_versicherungssumme', label: 'Gewuenschte Versicherungssumme', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'beguenstigung', label: 'Beguenstigung', typ: F.TEXTAREA },
    { key: 'bvg_ausweis_vorhanden', label: 'BVG-Ausweis vorhanden', typ: F.BOOLEAN },
  ]),
  '92000': felder('92000', [
    { key: 'freizuegigkeitsguthaben_bandbreite', label: 'Freizuegigkeitsguthaben-Bandbreite', typ: F.SELECT, options: ['fz_bis_50k', 'fz_50k_100k', 'fz_100k_250k', 'fz_250k_500k', 'fz_ueber_500k'] },
    { key: 'datum_letzter_arbeitgeberaustritt', label: 'Datum letzter Arbeitgeberaustritt', typ: F.DATE },
  ]),
  '93000': felder('93000', [
    { key: 'anzahl_versicherte', label: 'Anzahl Versicherte', typ: F.NUMBER },
    { key: 'lohnsumme', label: 'Lohnsumme', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'vorsorgeplan_typ', label: 'Vorsorgeplan-Typ', typ: F.SELECT, options: ['plan_bvg_minimum', 'plan_umhuellend', 'plan_kader_1e'] },
    { key: 'deckungsgrad_kasse_prozent', label: 'Deckungsgrad Kasse', typ: F.NUMBER, einheit: '%' },
  ]),
  '94000': felder('94000', [
    { key: 'hypothekarvolumen_bandbreite', label: 'Hypothekarvolumen-Bandbreite', typ: F.SELECT, options: ['hypo_bis_500k', 'hypo_500k_1m', 'hypo_1m_2m', 'hypo_ueber_2m'] },
    { key: 'objektwert', label: 'Objektwert', typ: F.NUMBER, einheit: 'CHF' },
    { key: 'belehnungsgrad_prozent', label: 'Belehnungsgrad', typ: F.NUMBER, einheit: '%' },
    { key: 'zinsfestlegung_laufzeit', label: 'Zinsfestlegung / Laufzeit', typ: F.SELECT, options: ['zins_variabel', 'zins_saron', 'zins_fest_bis_5j', 'zins_fest_ueber_5j'] },
  ]),
  // ═══ "Übrige" catch-all codes (Q18): one Freitext field each ═════════════════════════
  '29900': felder('29900', [{ key: 'risikobeschreibung', label: 'Risikobeschreibung', typ: F.TEXTAREA }]),
  '39900': felder('39900', [{ key: 'risikobeschreibung', label: 'Risikobeschreibung', typ: F.TEXTAREA }]),
  '43000': felder('43000', [{ key: 'risikobeschreibung', label: 'Risikobeschreibung', typ: F.TEXTAREA }]),
  '59900': felder('59900', [{ key: 'risikobeschreibung', label: 'Risikobeschreibung', typ: F.TEXTAREA }]),
  '69900': felder('69900', [{ key: 'risikobeschreibung', label: 'Risikobeschreibung', typ: F.TEXTAREA }]),
  '79900': felder('79900', [{ key: 'risikobeschreibung', label: 'Risikobeschreibung', typ: F.TEXTAREA }]),
  '89900': felder('89900', [{ key: 'risikobeschreibung', label: 'Risikobeschreibung', typ: F.TEXTAREA }]),
  '99900': felder('99900', [{ key: 'risikobeschreibung', label: 'Risikobeschreibung', typ: F.TEXTAREA }]),
};

// ── SOLL_DECKUNG_KATALOG (the per-branch Soll-Deckung regelwerk, Ch 5) ───────────

/**
 * One Soll-Deckung rule in the code catalog (the Soll). `rule_key` is the stable per-rule slug
 * (the stored row key within a policy); `branchencode` is the IGB2B code it applies to. The same
 * `rule_key` may repeat across branchencodes (uebrige_deckung on each "Übrige" code) — within a
 * single policy (one branchencode) it is unique, which is what the policy_target_coverage unique
 * arbiter needs.
 */
export interface SollDeckungRegel {
  rule_key: string;
  branchencode: string;
  label: string;
  pflicht_status: SollPflichtStatus;
  rechtsgrundlage?: string;
  ziel_text: string;
  ziel_default_chf?: number;
}

const P = SOLL_PFLICHT_STATUS;

/** The 8 "Übrige" catch-all branchencodes (each carries one optional uebrige_deckung rule). */
const UEBRIGE_CODES = ['29900', '39900', '43000', '59900', '69900', '79900', '89900', '99900'] as const;

/**
 * The complete Soll-Deckung catalog: 49 leaf rules + 8 uebrige_deckung rows = 57 rules (Ch 5).
 * Default CHF only where Ch 5 gives a number (Q20).
 */
export const SOLL_DECKUNG_KATALOG: readonly SollDeckungRegel[] = [
  { rule_key: 'mfz_haftpflicht', branchencode: '11000', label: 'Motorfahrzeug-Haftpflicht', pflicht_status: P.PFLICHT, rechtsgrundlage: 'SVG Art. 63', ziel_text: 'Gesetzlich vorgeschriebene Motorfahrzeug-Haftpflichtdeckung.' },
  { rule_key: 'mfz_kasko', branchencode: '11000', label: 'Motorfahrzeug-Kasko', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Teil- oder Vollkasko je nach Fahrzeugwert.' },
  { rule_key: 'luft_halterhaftpflicht', branchencode: '12000', label: 'Luftfahrzeug-Halterhaftpflicht', pflicht_status: P.PFLICHT, rechtsgrundlage: 'LFG', ziel_text: 'Gesetzlich vorgeschriebene Halterhaftpflicht fuer Luftfahrzeuge.' },
  { rule_key: 'luft_kasko', branchencode: '12000', label: 'Luftfahrzeug-Kasko', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Kaskodeckung fuer das Luftfahrzeug.' },
  { rule_key: 'boot_haftpflicht', branchencode: '13000', label: 'Wasserfahrzeug-Haftpflicht', pflicht_status: P.BEDINGT_PFLICHT, ziel_text: 'Haftpflicht fuer Wasserfahrzeuge; kantonal teils obligatorisch.' },
  { rule_key: 'boot_kasko', branchencode: '13000', label: 'Wasserfahrzeug-Kasko', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Kaskodeckung fuer das Wasserfahrzeug.' },
  { rule_key: 'moto_haftpflicht', branchencode: '14000', label: 'Motorrad-Haftpflicht', pflicht_status: P.PFLICHT, ziel_text: 'Gesetzlich vorgeschriebene Motorrad-Haftpflichtdeckung.' },
  { rule_key: 'betriebshaftpflicht_deckung', branchencode: '21000', label: 'Betriebshaftpflicht', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Betriebs- und Produktehaftpflicht nach Marktstandard.', ziel_default_chf: 5000000 },
  { rule_key: 'gebaeudehaftpflicht_deckung', branchencode: '22000', label: 'Gebaeudehaftpflicht', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Haftpflicht aus Gebaeudeeigentum.' },
  { rule_key: 'vermoegensschaden_hp_deckung', branchencode: '23000', label: 'Vermoegensschadenhaftpflicht', pflicht_status: P.BEDINGT_PFLICHT, ziel_text: 'Berufliche Vermoegensschadenhaftpflicht; je nach Taetigkeit obligatorisch.' },
  { rule_key: 'do_deckung', branchencode: '24000', label: 'Organhaftpflicht (D&O)', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Organhaftpflicht fuer VR und Geschaeftsleitung.' },
  { rule_key: 'bauherren_hp_deckung', branchencode: '25000', label: 'Bauherrenhaftpflicht', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Bauherrenhaftpflicht fuer die Dauer des Bauvorhabens.' },
  { rule_key: 'privathaftpflicht_deckung', branchencode: '26000', label: 'Privathaftpflicht', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Privathaftpflicht nach Marktstandard.', ziel_default_chf: 5000000 },
  { rule_key: 'hundehalter_hp', branchencode: '26000', label: 'Hundehalterhaftpflicht', pflicht_status: P.BEDINGT_PFLICHT, ziel_text: 'Hundehalterhaftpflicht; kantonal teils obligatorisch.' },
  { rule_key: 'einzelunfall_deckung', branchencode: '31000', label: 'Einzelunfallversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Einzelunfalldeckung bei UVG-Luecke.' },
  { rule_key: 'kollektivunfall_deckung', branchencode: '33000', label: 'Kollektivunfallversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Kollektivunfalldeckung fuer das Personal.' },
  { rule_key: 'landw_unfall_deckung', branchencode: '34000', label: 'Landwirtschaftliche Unfallversicherung', pflicht_status: P.BEDINGT_PFLICHT, ziel_text: 'Unfalldeckung fuer landwirtschaftliche Betriebe.' },
  { rule_key: 'schuelerunfall_deckung', branchencode: '35000', label: 'Schuelerunfallversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Unfalldeckung fuer Schueler.' },
  { rule_key: 'uvgz_deckung', branchencode: '36000', label: 'Unfallzusatzversicherung (UVGZ)', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Zusatzdeckung ueber das UVG-Obligatorium hinaus.' },
  { rule_key: 'uvg_deckung', branchencode: '38000', label: 'Obligatorische Unfallversicherung (UVG)', pflicht_status: P.PFLICHT, rechtsgrundlage: 'Art. 1a UVG', ziel_text: 'Gesetzlich vorgeschriebene Unfalldeckung ab erstem Mitarbeitenden.' },
  { rule_key: 'kvg_grundversicherung', branchencode: '41000', label: 'Krankenversicherung KVG', pflicht_status: P.PFLICHT, rechtsgrundlage: 'KVG', ziel_text: 'Gesetzliches Krankenversicherungs-Obligatorium.' },
  { rule_key: 'kvg_zusatz', branchencode: '41000', label: 'Krankenzusatzversicherung', pflicht_status: P.OPTIONAL, ziel_text: 'Freiwillige Zusatzdeckungen nach Bedarf.' },
  { rule_key: 'ktg_deckung', branchencode: '42000', label: 'Kollektiv-Krankentaggeld', pflicht_status: P.EMPFEHLUNG, rechtsgrundlage: 'OR Art. 324a', ziel_text: 'Krankentaggeld zur Absicherung der Lohnfortzahlung.' },
  { rule_key: 'cargo_deckung', branchencode: '51000', label: 'Warentransportversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Transportdeckung fuer die versicherten Waren.' },
  { rule_key: 'valoren_deckung', branchencode: '52000', label: 'Valorenversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Deckung fuer Wertgegenstaende im Transport.' },
  { rule_key: 'reisegepaeck_deckung', branchencode: '53000', label: 'Reisegepaeckversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Deckung des Reisegepaecks.' },
  { rule_key: 'frachtfuehrer_deckung', branchencode: '54000', label: 'Frachtfuehrerversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Haftung des Frachtfuehrers fuer das Transportgut.' },
  { rule_key: 'spediteur_hp_deckung', branchencode: '55000', label: 'Spediteurhaftpflicht', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Haftpflicht des Spediteurs.' },
  { rule_key: 'transport_kasko_deckung', branchencode: '56000', label: 'Transport-Kaskoversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Kaskodeckung fuer Transportmittel und Ware.' },
  { rule_key: 'kunst_deckung', branchencode: '58000', label: 'Kunstgegenstaendeversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Allgefahrendeckung fuer Kunstgegenstaende.' },
  { rule_key: 'gebaeude_deckung', branchencode: '60000', label: 'Gebaeudeversicherung', pflicht_status: P.BEDINGT_PFLICHT, ziel_text: 'Gebaeudedeckung; in Monopol-Kantonen obligatorisch, Neuwertbasis.' },
  { rule_key: 'hausrat_deckung', branchencode: '60000', label: 'Hausrat-/Inventarversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Hausrat- bzw. Inventardeckung auf Neuwertbasis.' },
  { rule_key: 'wertsachen_deckung', branchencode: '65000', label: 'Wertsachenversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Separate Deckung fuer Wertsachen.' },
  { rule_key: 'bu_deckung', branchencode: '67000', label: 'Betriebsunterbrechungsversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Absicherung des Rohertrags bei Betriebsunterbruch.' },
  { rule_key: 'bauwesen_deckung', branchencode: '71000', label: 'Bauwesenversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Bauwesendeckung fuer die Bauphase.' },
  { rule_key: 'montage_deckung', branchencode: '72000', label: 'Montageversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Montagedeckung fuer die Montagephase.' },
  { rule_key: 'maschinen_deckung', branchencode: '73000', label: 'Maschinenversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Maschinenbruch- und Schadendeckung.' },
  { rule_key: 'edv_deckung', branchencode: '74000', label: 'EDV-Versicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Deckung fuer EDV-Anlagen.' },
  { rule_key: 'ata_deckung', branchencode: '75000', label: 'Allg. technische Anlagen (ATA)', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Deckung fuer allgemeine technische Anlagen.' },
  { rule_key: 'kredit_deckung', branchencode: '81000', label: 'Kreditversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Absicherung des Debitorenausfallrisikos.' },
  { rule_key: 'rechtsschutz_deckung', branchencode: '82000', label: 'Rechtsschutzversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Rechtsschutz fuer die benoetigten Rechtsgebiete.' },
  { rule_key: 'reise_assistance_deckung', branchencode: '83500', label: 'Reise- und Assistanceversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Assistance- und Reisedeckung nach Reiseaktivitaet.' },
  { rule_key: 'epidemie_deckung', branchencode: '85000', label: 'Epidemieversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Absicherung des Umsatzausfalls bei Epidemie/Pandemie.' },
  { rule_key: 'cyber_deckung', branchencode: '87000', label: 'Cyberversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Cyber-Deckung bei IT-Abhaengigkeit.' },
  { rule_key: 'kaution_deckung', branchencode: '88000', label: 'Garantie-/Kautionsversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Garantie- und Kautionsdeckung nach Vertragsart.' },
  { rule_key: 'einzelleben_deckung', branchencode: '91000', label: 'Einzellebensversicherung', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Todesfall-/Erwerbsunfaehigkeitsdeckung nach Bedarf.' },
  { rule_key: 'freizuegigkeit_platzierung', branchencode: '92000', label: 'Freizuegigkeitspolice', pflicht_status: P.BEDINGT_PFLICHT, rechtsgrundlage: 'FZG', ziel_text: 'Platzierung des Freizuegigkeitsguthabens.' },
  { rule_key: 'bvg_deckung', branchencode: '93000', label: 'Kollektivlebensversicherung BVG', pflicht_status: P.PFLICHT, ziel_text: 'Berufliche Vorsorge ab BVG-Eintrittsschwelle.' },
  { rule_key: 'hypo_todesfallrisiko', branchencode: '94000', label: 'Hypothekar-Todesfallrisiko', pflicht_status: P.EMPFEHLUNG, ziel_text: 'Absicherung der Hypothek im Todesfall.' },
  // uebrige_deckung on each of the 8 "Übrige" catch-all codes
  ...UEBRIGE_CODES.map((code) => ({
    rule_key: 'uebrige_deckung',
    branchencode: code,
    label: 'Uebrige Deckung',
    pflicht_status: P.OPTIONAL,
    ziel_text: 'Weitere Deckung nach individuellem Bedarf.',
  })),
];

// ── Pure helpers ────────────────────────────────────────────────────────────────

/** The Ist-field definitions for one branchencode ([] for unknown or a pure family head). */
export function branchenFelderFuer(code: string | null | undefined): readonly BranchenFeldDefinition[] {
  if (!code) return [];
  return BRANCHEN_IST_KATALOG[code] ?? [];
}

/** The Soll-Deckung rules for one branchencode (in catalog order; [] for unknown). */
export function sollRegelnFuer(code: string | null | undefined): readonly SollDeckungRegel[] {
  if (!code) return [];
  return SOLL_DECKUNG_KATALOG.filter((r) => r.branchencode === code);
}

/** Is the code a numeric IGB2B family head (first digit x 10000, e.g. 20000, 60000, 90000)? */
export function isIgb2bFamilyHead(code: string): boolean {
  return Number(code) % 10000 === 0;
}

/**
 * The leaf (non-head) codes of `code`'s IGB2B family, drawn from `all` (e.g. the catalog keys).
 * Used by the "Sparte präzisieren" picker when a policy carries a family-head code.
 */
export function igb2bLeafCodesOfFamily(code: string, all: readonly string[]): string[] {
  const family = igb2bFamilyOf(code);
  return all.filter((c) => igb2bFamilyOf(c) === family && !isIgb2bFamilyHead(c));
}

/** Round to 2 decimals (money / percent). */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Compute the Soll-vs-Ist gap. Both non-null and target>0:
 *   gap_amount  = round2(target - actual)
 *   gap_percent = round2(gap_amount / target * 100)
 *   status      = 'gap' if gap_amount>0; 'overinsured' if gap_percent < -20; else 'covered'.
 * Otherwise (missing value or non-positive target) → null/null/'unknown'.
 */
export function computeDeckungGap(
  target: number | null,
  actual: number | null,
): { gap_amount: number | null; gap_percent: number | null; status: DeckungStatus } {
  if (target === null || actual === null || target <= 0) {
    return { gap_amount: null, gap_percent: null, status: DECKUNG_STATUS.UNKNOWN };
  }
  const gap_amount = round2(target - actual);
  const gap_percent = round2((gap_amount / target) * 100);
  let status: DeckungStatus;
  if (gap_amount > 0) status = DECKUNG_STATUS.GAP;
  else if (gap_percent < -DECKUNG_UEBERVERSICHERT_SCHWELLE_PROZENT) status = DECKUNG_STATUS.OVERINSURED;
  else status = DECKUNG_STATUS.COVERED;
  return { gap_amount, gap_percent, status };
}

/**
 * Derive the Ampel colour of a Soll-Deckung row:
 *   not_applicable        -> GRAU
 *   covered | overinsured -> GRUEN
 *   gap                   -> GELB if gap_percent < 20, else ROT (null gap_percent -> ROT)
 *   unknown               -> ROT if pflicht|bedingt_pflicht, else GELB
 */
export function deriveDeckungAmpel(input: {
  status: DeckungStatus;
  pflicht_status: SollPflichtStatus;
  gap_percent: number | null;
}): DeckungAmpel {
  switch (input.status) {
    case DECKUNG_STATUS.NOT_APPLICABLE:
      return DECKUNG_AMPEL.GRAU;
    case DECKUNG_STATUS.COVERED:
    case DECKUNG_STATUS.OVERINSURED:
      return DECKUNG_AMPEL.GRUEN;
    case DECKUNG_STATUS.GAP:
      return input.gap_percent !== null && input.gap_percent < DECKUNG_GAP_GELB_SCHWELLE_PROZENT
        ? DECKUNG_AMPEL.GELB
        : DECKUNG_AMPEL.ROT;
    case DECKUNG_STATUS.UNKNOWN:
    default:
      return input.pflicht_status === SOLL_PFLICHT_STATUS.PFLICHT ||
        input.pflicht_status === SOLL_PFLICHT_STATUS.BEDINGT_PFLICHT
        ? DECKUNG_AMPEL.ROT
        : DECKUNG_AMPEL.GELB;
  }
}

// ── Wire types (snake_case) ──────────────────────────────────────────────────────

/** One insured-object Ist-attribute set (NULL ref = the policy-level set). */
export interface PoliceRisikoObjekt {
  /** The policy_risk_attributes row id — the key the DELETE-object route takes. */
  id: string;
  insurance_object_ref: string | null;
  object_label: string | null;
  attributes: Record<string, unknown>;
  source: PoliceRisikoSource;
  filled: number;
  total: number;
  updated_at: string;
}

/** GET /policies/:id/risk-attributes response. */
export interface PoliceRisikoResponse {
  policy_id: string;
  subject_type: SubjectKind;
  subject_id: string;
  /** policy.igb2b_code; null when the policy is not yet classified. */
  branchencode: string | null;
  /** true when branchencode is a pure family head (empty catalog → "Sparte präzisieren"). */
  is_family_head: boolean;
  /** Leaf codes to pick from when branchencode is a family head. */
  leaf_codes: string[];
  /** The branch Ist catalog for branchencode ([] for null / family head). */
  felder: BranchenFeldDefinition[];
  /** One entry per insured object; the NULL-ref entry is the policy-level set. */
  objekte: PoliceRisikoObjekt[];
  /** 91000 Privatperson: read-only mirror of the Kunde gesundheitspruefung_status (present only when visible). */
  gesundheitspruefung_status?: GesundheitspruefungStatus | null;
  /** Gated keys suppressed on this read (role / consent). */
  gated_fields_suppressed: string[];
}

/** PATCH /policies/:id/risk-attributes body (merge-patch of one object's attributes). */
export interface PoliceRisikoPatchBody {
  /** The insured-object ref; omit / null = the policy-level set. */
  insurance_object_ref?: string | null;
  object_label?: string | null;
  attributes?: Record<string, unknown>;
  expected_updated_at?: string;
}

/** One Soll-Deckung row (catalog rule merged with the stored evaluation + derived Ampel). */
export interface DeckungItem {
  rule_key: string;
  branchencode: string;
  label: string;
  pflicht_status: SollPflichtStatus;
  rechtsgrundlage: string | null;
  ziel_text: string;
  ziel_default_chf: number | null;
  /** The target_coverage_catalog row id (P2 Slice 3 FK); null for a code-catalog rule. */
  rule_id: string | null;
  insurance_object_ref: string | null;
  target_value: string | null;
  actual_value: string | null;
  gap_amount: string | null;
  gap_percent: string | null;
  status: DeckungStatus;
  evaluation_source: DeckungEvaluationSource;
  notiz: string | null;
  /** P2 Slice 12: promoted Soll-Selbstbehalt (numeric as text) / Soll-Prioritaet (read-through; Q109). */
  selbstbehalt: string | null;
  prioritaet: number | null;
  ampel: DeckungAmpel;
}

/** GET /policies/:id/target-coverage response. */
export interface DeckungResponse {
  policy_id: string;
  branchencode: string | null;
  items: DeckungItem[];
}

/** PUT /policies/:id/target-coverage/:ruleKey body (true merge). */
export interface DeckungPutBody {
  insurance_object_ref?: string | null;
  target_value?: number | null;
  actual_value?: number | null;
  /** Only the broker-set terminal states are settable here; gap/covered/overinsured are derived. */
  status?: 'not_applicable' | 'unknown';
  notiz?: string | null;
}
